import React from "react";
import PropTypes from "prop-types";
import { useSelector, useStore, shallowEqual } from "react-redux";
import { Button, Typography, Zoom } from "@material-ui/core";
import _ from "lodash";

import * as c from "../../../constants";
import { FindEntity } from "../../../state/slices/entities";
import { selectAllOrgTreeEntities } from "../../../state/slices/OrgTree";
import { useEnqueueSnackbar } from "../../../hooks/useEnqueueSnackbar";
import useHubContext from "../../../hooks/useHubContext";
import { useConfirmationDialog } from "../../../hooks/useConfirmationDialog";
import { useCoreEntityContext } from "../../../hooks/useCoreEntitySlice";

/**
 * Traverses the legacy entity tree (state.entities.EntityList) to collect
 * all device-type descendants under the given entityID.
 */
const collectFromLegacyTree = (entityID, state) => {
  const root = FindEntity(state.entities?.EntityList ?? [], entityID);
  if (!root) return [];

  const devices = [];
  const traverse = (entity) => {
    if (!entity) return;
    if (entity.entitytype === c.ENTITY_TYPE.Device) {
      devices.push(entity.entityid);
    }
    if (entity.children && entity.children.length > 0) {
      entity.children.forEach(traverse);
    }
  };

  if (root.children) {
    root.children.forEach(traverse);
  }
  return devices;
};

/**
 * Traverses the OrgTree flat entity list using parententityid relationships
 * to collect all device-type descendants under the given entityID.
 */
const collectFromOrgTree = (entityID, state) => {
  const allEntities = selectAllOrgTreeEntities(state);
  const devices = [];

  const traverse = (parentId) => {
    const children = _.filter(allEntities, { parententityid: parentId });
    children.forEach((child) => {
      if (child.typeid === c.ENTITY_TYPE.Device) {
        devices.push(child.entityid);
      } else {
        traverse(child.entityid);
      }
    });
  };

  traverse(entityID);
  return devices;
};

/**
 * Finds all device-type entity IDs that are descendants of the given entityID.
 *
 * @param {string} entityID - The root entity to search under
 * @param {object} state - The Redux state
 * @param {boolean} useCoreEntitySlice - Whether to use the CoreEntity/OrgTree slice or legacy slice
 * @returns {string[]} - Array of device entity IDs
 */
export const collectDeviceDescendants = (entityID, state, useCoreEntitySlice) => {
  if (useCoreEntitySlice) {
    return collectFromOrgTree(entityID, state);
  }
  return collectFromLegacyTree(entityID, state);
};

/**
 * Allowed emails for the Reboot All Devices feature.
 * Only users with these email addresses (AND admin permission) can see the button.
 */
const ALLOWED_REBOOT_ALL_EMAILS = [
  "prajwol.shrestha@amanomcgann.com",
  "jason.gross@amanomcgann.com",
  "vineet.kumar@amanomcgann.com",
  "tanner.brunscheon@amanomcgann.com",
  "chad.christiansen@amanomcgann.com",
];

const EntityRebootAllButton = ({ entityID, parentEntityID, ...props }) => {
  const useCoreEntitySlice = useCoreEntityContext();
  const isAdmin = useSelector((state) => state?.user?.isAdmin, shallowEqual);
  const currentUser = useSelector((state) => state.user, shallowEqual);
  const enqueueSnackbar = useEnqueueSnackbar();
  const { portalHub } = useHubContext();
  const { textConfirmation } = useConfirmationDialog();
  const store = useStore();

  // Hide button if:
  // 1. User is not admin
  // 2. Entity is top-level (no parent) — we don't allow rebooting all devices at the root level
  // 3. User email is not in the authorized whitelist 
  const userEmail = currentUser?.EmailAddress?.toLowerCase();
  if (!isAdmin || !parentEntityID || !userEmail || !ALLOWED_REBOOT_ALL_EMAILS.includes(userEmail)) return null;

  const handleClick = async () => {
    const confirmed = await textConfirmation({
      title: "REBOOT ALL DEVICES?",
      message: (
        <Typography>
          This will send a reboot command to all devices under this entity. All
          devices will be out of operation until the reboot process is complete.
        </Typography>
      ),
      textConfirmation: "REBOOT ALL DEVICES",
    });

    if (!confirmed) return;

    const deviceIds = collectDeviceDescendants(entityID, store.getState(), useCoreEntitySlice);

    if (deviceIds.length === 0) {
      enqueueSnackbar("No devices found under this entity", {
        variant: "info",
        autoHideDuration: 5000,
      });
      return;
    }

    deviceIds.forEach((deviceId) => {
      portalHub.invoke(c.PORTAL_TRIGGER, {
        entityID: deviceId,
        topic: "REBOOT",
        method: "rebootdevice",
        action: "reboot",
        message: JSON.stringify({
          user: currentUser.Username,
          timeOfChange: Date.now(),
        }),
      });
    });

    enqueueSnackbar(`Reboot sent to ${deviceIds.length} devices`, {
      variant: "success",
      autoHideDuration: 5000,
      TransitionComponent: Zoom,
    });
  };

  return (
    <Button
      {...props}
      onClick={handleClick}
    >
      REBOOT ALL DEVICES
    </Button>
  );
};

EntityRebootAllButton.defaultProps = { entityID: null, parentEntityID: null };
EntityRebootAllButton.propTypes = { entityID: PropTypes.string, parentEntityID: PropTypes.string };

export default EntityRebootAllButton;
